import React, { useState } from "react";
import {
  mdiBookCheckOutline,
  mdiBookOffOutline,
  mdiBookEditOutline,
  mdiBookOutline,
  mdiBookSyncOutline,
} from "@mdi/js";
import { Tooltip } from "react-tooltip";
import { cn } from "../../styles";
import { FieldType } from "../../constants";
import { ThemeColor } from "../../props";
import { ButtonVariant } from "../../atoms/Button/ButtonBase";
import { Button, Typography } from "../../atoms";
import { Dialog } from "../../layouts/Dialog";
import { LocalizationScope } from "../../util/LocalizationScope";
import { FormField } from "../../molecules";

const WIDE_TRAIT_TYPES = new Set<string>([
  FieldType.ORDERING,
  FieldType.TEXTAREA,
  FieldType.SELECT_MULTI,
]);

function TraitList({ children, gutterBottom }) {
  return (
    <div className={gutterBottom ? "mb-3" : ""}>
      <div className="flex flex-wrap gap-2 justify-start items-stretch">
        {children}
      </div>
    </div>
  );
}

function PresetActions({ id, preset, meta, errors, onLinkFieldPreset, onOpenDialog }) {
  if (meta?.preset) {
    return (
      <>
        <Button
          iconPath={mdiBookEditOutline}
          color={ThemeColor.primary}
          variant={ButtonVariant.text}
          onClick={onOpenDialog}
          data-tooltip-id={`pss-preset-edit-tp-${id}`}
        />
        <Tooltip id={`pss-preset-edit-tp-${id}`}>
          <Typography.Span content={{ local: "EditPreset" }} />
        </Tooltip>
      </>
    );
  }

  const hasLinkedPreset = preset[id];

  return (
    <>
      <Button
        iconPath={hasLinkedPreset ? mdiBookSyncOutline : mdiBookOutline}
        variant={ButtonVariant.text}
        disabled={!hasLinkedPreset && errors && errors.length > 0}
        onClick={() => onLinkFieldPreset(id)}
        data-tooltip-id={`pss-preset-set-tp-${id}`}
      />
      <Tooltip id={`pss-preset-set-tp-${id}`}>
        <Typography.Span content={{ local: hasLinkedPreset ? "LinkPreset" : "SetPreset" }} />
      </Tooltip>
    </>
  );
}

function FieldWithPreset({ field, preset, onChangeField, onLinkFieldPreset, onUnlinkFieldPreset, onChangePresetField, restFieldProps, fieldDisabled }) {
  const [presetOpen, setPresetOpen] = useState(false);
  // Local state for the dialog — editing here never touches the form state.
  const [dialogValue, setDialogValue] = useState<any>(null);
  const [dialogData, setDialogData] = useState<any>(null);

  const handleOpen = () => {
    setDialogValue(field.value);
    setDialogData(field.data);
    setPresetOpen(true);
  };

  const handleDialogChange = (_id: string, value: any, opts: any = {}) => {
    setDialogValue(value);
    setDialogData(opts.data ?? null);
  };

  const presetActions = (
    <PresetActions
      id={field.id}
      preset={preset}
      meta={field.meta}
      errors={field.errors}
      onLinkFieldPreset={onLinkFieldPreset}
      onOpenDialog={handleOpen}
    />
  );

  const renderPresetActions = () => presetActions;

  const fieldNode = (
    <FormField
      keepHeight
      {...field}
      disabled={fieldDisabled || field.meta?.preset}
      {...restFieldProps}
      onChangeField={onChangeField}
      renderPresetActions={renderPresetActions}
    />
  );

  if (field.readOnly) {
    return <div className="mr-[72px]">{fieldNode}</div>;
  }

  if (field.variant === "trait") {
    return (
      <>
        {fieldNode}
        <PresetDialog
          field={{ ...field, value: dialogValue, data: dialogData }}
          open={presetOpen}
          onCancel={() => setPresetOpen(false)}
          onClose={() => setPresetOpen(false)}
          onUnlinkFieldPreset={onUnlinkFieldPreset}
          onChangePresetField={onChangePresetField}
          onChangeField={handleDialogChange}
          restFieldProps={restFieldProps}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-start">
        <div className="grow">{fieldNode}</div>
        <div className="mt-6 ml-3">{presetActions}</div>
      </div>
      <PresetDialog
        field={{ ...field, value: dialogValue, data: dialogData }}
        open={presetOpen}
        onCancel={() => setPresetOpen(false)}
        onClose={() => setPresetOpen(false)}
        onUnlinkFieldPreset={onUnlinkFieldPreset}
        onChangePresetField={onChangePresetField}
        onChangeField={handleDialogChange}
        restFieldProps={restFieldProps}
      />
    </>
  );
}

function PresetDialog({ field, open, onCancel, onClose, onUnlinkFieldPreset, onChangePresetField, onChangeField, restFieldProps }) {
  return (
    <LocalizationScope scope={["PresetDialog", `PresetDialog::${field.localKey || field.id}`, ""]}>
      <Dialog
        dialogId={`preset-dialog-${field.id}`}
        open={open}
        onClose={onClose}
        minWidth={400}
      >
        <Dialog.Header>
          <Dialog.Title content={{ local: "Title" }} />
        </Dialog.Header>
        <Dialog.Content>
          <FormField
            {...field}
            {...restFieldProps}
            onChangeField={onChangeField}
          />
        </Dialog.Content>
        <Dialog.Footer>
          <Dialog.Actions>
            <Button onClick={onCancel}>
              <Typography.Span content={{ local: "Cancel" }} />
            </Button>
            <Button
              iconPath={mdiBookOffOutline}
              variant={ButtonVariant.outlined}
              onClick={() => {
                onUnlinkFieldPreset(field.id, field.value, field.data);
                onClose();
              }}
            >
              <Typography.Span content={{ local: "UnlinkField" }} />
            </Button>
            <Button
              iconPath={mdiBookCheckOutline}
              color={ThemeColor.primary}
              onClick={() => {
                onChangePresetField(field.id, field.value, field.data);
                onClose();
              }}
            >
              <Typography.Span content={{ local: "UpdateValue" }} />
            </Button>
          </Dialog.Actions>
        </Dialog.Footer>
      </Dialog>
    </LocalizationScope>
  );
}

export default function FieldSet({
  className,
  fields,
  compact = true,
  disabled,
  autoFocus,
  preset,
  onLinkFieldPreset,
  onUnlinkFieldPreset,
  onChangePresetField,
  onChangeField,
  ...fieldProps
}: any) {
  const traits = fields.filter((f) => f.variant === "trait");
  const regularFields = fields
    .filter((f) => f.variant !== "trait")
    .reduce(
      (acc, f) =>
        f.merge
          ? [...acc.slice(0, -1), [...acc[acc.length - 1], f]]
          : [...acc, [f]],
      []
    );

  let autoFocusId = fields.find(
    (field) => !field.disabled && field.autoFocus
  )?.id;
  if (!autoFocusId) autoFocusId = traits.find((field) => !field.disabled)?.id;
  if (!autoFocusId)
    autoFocusId = regularFields.flat().find((field) => !field.disabled)?.id;

  const renderField = (field, fieldDisabled) => {
    const baseProps = {
      autoFocus: autoFocus && field.id === autoFocusId,
    };

    if (preset) {
      return (
        <FieldWithPreset
          key={field.id}
          field={field}
          preset={preset}
          onChangeField={onChangeField}
          onLinkFieldPreset={onLinkFieldPreset}
          onUnlinkFieldPreset={onUnlinkFieldPreset}
          onChangePresetField={onChangePresetField}
          restFieldProps={{ ...fieldProps, ...baseProps }}
          fieldDisabled={fieldDisabled}
        />
      );
    }

    return (
      <FormField
        key={field.id}
        keepHeight={!compact}
        {...field}
        disabled={fieldDisabled}
        {...baseProps}
        {...fieldProps}
        onChangeField={onChangeField}
      />
    );
  };

  return (
    <div className={className}>
      {traits.length > 0 && (
        <TraitList gutterBottom>
          {traits.map((field) => (
            <div key={field.id} className={cn(WIDE_TRAIT_TYPES.has(field.type) && "basis-full")}>
              {renderField(field, field.disabled || disabled)}
            </div>
          ))}
        </TraitList>
      )}
      <div>
        {regularFields.map((fieldArr, idx) => (
          <div className="flex w-full mb-4 last:mb-0" key={idx}>
            {fieldArr.map((field) => (
              <div
                key={field.id}
                className="basis-0 grow ml-1 mr-1 first:ml-0 last:mr-0"
              >
                {renderField(field, field.disabled || disabled)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
